import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { STATS, BRAND_LOGOS, IMAGES } from "@/lib/content";

export const SectionHeading = ({ eyebrow, title, intro, light = false, className = "" }) => (
  <Reveal className={`max-w-3xl ${className}`}>
    {eyebrow && <p className={`eyebrow ${light ? "text-gold" : "text-gold-dark"}`}>{eyebrow}</p>}
    <h2 className={`mt-5 font-display text-4xl font-medium leading-[1.05] sm:text-5xl ${light ? "text-ivory" : "text-navy"}`}>{title}</h2>
    {intro && <p className={`mt-6 text-base leading-relaxed md:text-lg ${light ? "text-ivory/70" : "text-slate-600"}`}>{intro}</p>}
  </Reveal>
);

export const PageHero = ({ eyebrow, title, intro, image, testid }) => (
  <section className="relative overflow-hidden bg-navy pb-20 pt-40 text-ivory md:pb-28 md:pt-48" data-testid={testid}>
    <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" fetchPriority="high" />
    <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/40" />
    <div className="container-x relative">
      <p className="eyebrow text-gold hero-in">{eyebrow}</p>
      <h1 className="hero-in mt-6 max-w-4xl font-display text-4xl font-medium leading-[1.02] sm:text-5xl lg:text-6xl" style={{ animationDelay: "120ms" }}>
        {title}
      </h1>
      {intro && (
        <p className="hero-in mt-8 max-w-2xl text-base leading-relaxed text-ivory/75 md:text-lg" style={{ animationDelay: "240ms" }}>
          {intro}
        </p>
      )}
      <div className="hero-in gold-rule mt-10" style={{ animationDelay: "360ms" }} />
    </div>
  </section>
);

export const StatsBand = () => (
  <section className="relative bg-navy py-20 text-ivory grain" data-testid="stats-band">
    <div className="container-x grid grid-cols-2 gap-y-12 md:grid-cols-3 lg:grid-cols-6">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 80} className="stat-cell px-4 lg:px-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <p className="font-display text-5xl font-medium text-gold md:text-6xl">{s.value}</p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.22em] text-ivory">{s.label}</p>
          <p className="mt-2 text-xs leading-relaxed text-ivory/55">{s.note}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

export const BrandLogo = ({ src, name, h = "h-10", className = "" }) => (
  <img src={src} alt={`${name} logo`} loading="lazy" decoding="async" className={`brand-logo ${h} w-auto max-w-full object-contain ${className}`} />
);

export const BrandLogos = () => (
  <div className="grid grid-cols-2 gap-px bg-navy/10 sm:grid-cols-3 lg:grid-cols-7" data-testid="brand-logos">
    {BRAND_LOGOS.map((b, i) => (
      <Reveal key={b.id} delay={i * 70} className="group flex h-32 items-center justify-center bg-ivory px-6" data-testid={`brand-logo-${b.id}`}>
        <BrandLogo src={b.src} name={b.name} h={b.h} />
      </Reveal>
    ))}
  </div>
);

export const CtaBand = ({ title = "Let's talk about your hotel.", text = "Whether you're a brand reviewer, an owner or a future colleague, we'd like to hear from you." }) => (
  <section className="relative overflow-hidden bg-navy py-28 text-ivory" data-testid="cta-band">
    <img src={IMAGES.skyline} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
    <div className="container-x relative flex flex-col items-start gap-10 md:flex-row md:items-end md:justify-between">
      <Reveal className="max-w-2xl">
        <p className="eyebrow text-gold">All In, On Site</p>
        <h2 className="mt-5 font-display text-4xl font-medium leading-tight sm:text-5xl">{title}</h2>
        <p className="mt-5 text-base text-ivory/75 md:text-lg">{text}</p>
      </Reveal>
      <Reveal delay={150}>
        <Link to="/contact" className="btn-gold" data-testid="cta-contact-button">
          Contact MSK <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export const StatusBadge = ({ status }) => {
  const map = {
    "Owned & Operated": "border-gold bg-gold text-navy",
    "Co-sponsored": "border-navy/30 bg-transparent text-navy",
    Proposed: "border-dashed border-gold-dark bg-transparent text-gold-dark",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.18em] ${map[status]}`} data-testid={`status-badge-${status.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      {status}
    </span>
  );
};

export const LeaderAvatar = ({ leader, size = "h-40 w-40" }) =>
  leader.photo ? (
    <img src={leader.photo} alt={`Portrait of ${leader.name}`} loading="lazy" decoding="async" width="160" height="160" className={`${size} rounded-full object-cover ring-1 ring-gold/50 ring-offset-4 ring-offset-ivory`} data-testid={`leader-photo-${leader.id}`} />
  ) : (
    <div className={`${size} flex items-center justify-center rounded-full bg-navy ring-1 ring-gold/50 ring-offset-4 ring-offset-ivory`} role="img" aria-label={`${leader.name} monogram`} data-testid={`leader-monogram-${leader.id}`}>
      <span className="font-display text-5xl font-medium tracking-wider text-gold">{leader.initials}</span>
    </div>
  );
